import {
  readWebsitePages,
  WEBSITE_PAGES_UNREAD,
  type WebsitePagesRead,
  type WebsitePagesRef,
} from "./websitePagesCard";
import {
  readComponentRelease,
  componentReleaseAfter,
  componentReleaseBlocked,
  COMPONENT_RELEASE_UNREAD,
  type ComponentReleaseRead,
  type ComponentReleaseRef,
} from "./componentReleaseCard";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { listBookingTypes, listCalendarInstances } from "./calendarClient";
import { useCanvasHead } from "./canvasHead";
import {
  readCanvasShareData,
  readShareGroup,
  type CanvasShareDataRead,
  type CanvasShareDataRef,
} from "./canvasShareClient";
import { listConversationMembers, listConversations } from "./commsClient";
import { crmPost } from "./crm";
import {
  describedApprovalRequest,
  deviceTimeZone,
  type ApprovalCalendarEvent,
  type ApprovalRequestInput,
  type ApprovalRequestContext,
  type BookingTypeRead,
  type PlainApprovalRequest,
} from "./gateInstancesClient";
import { readSubscriptionPlan, type SubscriptionPlanRead } from "./subscriptionPlanClient";
import { readCommercialChange, type CommercialChangeRead } from "./commercialChangeClient";
import { readMemberChange, type MemberChangeRead } from "./memberChangeClient";
import { readConsultingCatalog, type ConsultingCatalogRead } from "./consultingClient";
import { useNeucleosWorldStore } from "./worldStore";

// W-f9a102e0 / W-6082d348: an approval names what it affects. The call names
// a Canvas or a conversation by id; this reads their names from the World
// (the Canvas head; the conversation's members) and the World's own name, for
// both surfaces that ask the person (the chat card and the Inbox), so each
// says the same thing. `reading` is true until every read has answered: the
// surface then withholds the plain words rather than show general ones that
// change a moment later (the walkthrough recorded "a Canvas" that way).

/** Longest a surface waits for a name before it says what it can without it. */
export const APPROVAL_CONTEXT_WAIT_MS = 5_000;

interface RecipientsRead {
  readonly key: string;
  readonly names: ReadonlyArray<string> | null;
}
const recipientsCache = new Map<string, Promise<ReadonlyArray<string> | null>>();

/** Everyone in the conversation except the person, by name; null when it cannot be read. */
export async function readConversationRecipients(
  worldKey: string,
  conversationId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ReadonlyArray<string> | null> {
  const [listed, members] = await Promise.all([
    listConversations(worldKey, fetchImpl),
    listConversationMembers(worldKey, fetchImpl),
  ]);
  if (!listed.ok || !members.ok) return null;
  const conversation = listed.conversations.find((row) => row.conversationId === conversationId);
  if (!conversation) return null;
  const known = new Map(members.members.map((member) => [member.actorId, member]));
  return conversation.participantIds.flatMap((actorId) => {
    const member = known.get(actorId);
    if (member?.isSelf) return [];
    return [member?.displayName ?? "someone who is no longer a member of this World"];
  });
}

function useConversationRecipients(
  worldKey: string | null,
  conversationId: string | null,
): { readonly names: ReadonlyArray<string> | null; readonly loaded: boolean } {
  const key = worldKey && conversationId ? JSON.stringify([worldKey, conversationId]) : null;
  const [read, setRead] = useState<RecipientsRead | null>(null);
  useEffect(() => {
    if (key === null || worldKey === null || conversationId === null) return;
    let pending = recipientsCache.get(key);
    if (!pending) {
      pending = readConversationRecipients(worldKey, conversationId).catch(() => null);
      recipientsCache.set(key, pending);
    }
    let live = true;
    void pending.then((names) => {
      // A failed read is asked again next time; a good one is shared.
      if (names === null && recipientsCache.get(key) === pending) recipientsCache.delete(key);
      if (live) setRead({ key, names });
    });
    return () => {
      live = false;
    };
  }, [key, worldKey, conversationId]);
  return read?.key === key && key !== null
    ? { names: read.names, loaded: true }
    : { names: null, loaded: key === null };
}

/**
 * W-96c0e692: the CRM contact or company, or the calendar event, a call
 * names, by name, read from the World under the person's own grant; null
 * when it cannot be read.
 */
export async function readApprovalRecordName(
  worldKey: string,
  plain: Pick<PlainApprovalRequest, "crmRecord" | "crmFollowUp" | "calendarOccurrence">,
): Promise<string | null> {
  return (await readApprovalRecord(worldKey, plain)).name;
}

/**
 * The record a call names (readApprovalRecordName), and for a calendar
 * occurrence also the event as the person's calendar lists it (W-4022cbfa: its
 * time, and whether it repeats), so the card says what is deleted and names a
 * series only when there is one.
 */
export async function readApprovalRecord(
  worldKey: string,
  plain: Pick<PlainApprovalRequest, "crmRecord" | "crmFollowUp" | "calendarOccurrence">,
): Promise<{
  readonly name: string | null;
  readonly calendarEvent?: ApprovalCalendarEvent | null;
}> {
  if (plain.calendarOccurrence) return readCalendarOccurrence(worldKey, plain.calendarOccurrence);
  return { name: await readCrmRecordName(worldKey, plain) };
}

async function readCrmRecordName(
  worldKey: string,
  plain: Pick<PlainApprovalRequest, "crmRecord" | "crmFollowUp">,
): Promise<string | null> {
  if (plain.crmRecord) {
    const { kind, id } = plain.crmRecord;
    const read = await crmPost<{
      contact?: { name?: unknown };
      company?: { name?: unknown };
    }>(
      worldKey,
      kind === "contact"
        ? { action: "contact", contactId: id }
        : { action: "company", companyId: id },
    );
    const name = (kind === "contact" ? read.contact : read.company)?.name;
    return typeof name === "string" && name !== "" ? name : null;
  }
  if (plain.crmFollowUp) {
    // A follow-up named by its id alone: the person's own scans say whose it is.
    const { activityId } = plain.crmFollowUp;
    for (const bucket of ["Upcoming", "Today", "Overdue"] as const) {
      let cursor: string | null = null;
      // Every page of the bucket, until the follow-up is found or the bucket ends.
      for (let page = 0; page < 50; page += 1) {
        const scan: {
          page?: ReadonlyArray<{ activityId?: unknown; targetName?: unknown }>;
          isDone?: boolean;
          continueCursor?: string;
        } = await crmPost(worldKey, {
          action: "followUps",
          bucket,
          timeZone: deviceTimeZone(),
          paginationOpts: { numItems: 100, cursor },
        });
        const row = scan.page?.find((item) => item.activityId === activityId);
        if (row)
          return typeof row.targetName === "string" && row.targetName !== ""
            ? row.targetName
            : null;
        if (scan.isDone !== false || typeof scan.continueCursor !== "string") break;
        cursor = scan.continueCursor;
      }
    }
    return null;
  }
  return null;
}

async function readCalendarOccurrence(
  worldKey: string,
  occurrence: NonNullable<PlainApprovalRequest["calendarOccurrence"]>,
): Promise<{ readonly name: string | null; readonly calendarEvent: ApprovalCalendarEvent | null }> {
  const { calendarEventId } = occurrence;
  // An occurrence named by its date is looked for around that date's noon (UTC).
  const around =
    "originalStartAt" in occurrence
      ? occurrence.originalStartAt
      : Date.parse(`${occurrence.occurrenceDate}T12:00:00Z`);
  if (!Number.isFinite(around)) return { name: null, calendarEvent: null };
  const day = 86_400_000;
  const listed = await listCalendarInstances(worldKey, {
    from: around - day,
    to: around + day,
  });
  if (!listed.ok) return { name: null, calendarEvent: null };
  const ofEvent = listed.instances.filter((row) => row.calendarEventId === calendarEventId);
  // The exact occurrence: by its original start, or by its date in the event's own zone.
  const exact = ofEvent.find((row) =>
    "originalStartAt" in occurrence
      ? row.originalStartAt === occurrence.originalStartAt
      : occurrenceDayIn(row.originalStartAt, row.timeZone) === occurrence.occurrenceDate,
  );
  return {
    // Its title, time and series only from the exact occurrence: another one's would be a guess.
    name: exact?.title ?? null,
    calendarEvent:
      exact === undefined
        ? null
        : {
            title: exact.title,
            startsAt: exact.startsAt,
            endsAt: exact.endsAt,
            recurring: exact.recurring,
          },
  };
}

/** An instant's date (YYYY-MM-DD) in a zone, or null for a zone this device does not know. */
function occurrenceDayIn(instant: number, timeZone: string): string | null {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(instant);
  } catch {
    return null;
  }
}
type ApprovalRecordRead = Awaited<ReturnType<typeof readApprovalRecord>>;
const recordNameCache = new Map<string, Promise<ApprovalRecordRead>>();

function useRecordName(
  worldKey: string | null,
  plain: PlainApprovalRequest | null,
): {
  readonly name: string | null;
  readonly calendarEvent: ApprovalCalendarEvent | null;
  readonly loaded: boolean;
} {
  const target = plain?.crmRecord ?? plain?.crmFollowUp ?? plain?.calendarOccurrence ?? null;
  const key = worldKey && target ? JSON.stringify([worldKey, target]) : null;
  const [read, setRead] = useState<{
    readonly key: string;
    readonly record: ApprovalRecordRead;
  } | null>(null);
  useEffect(() => {
    if (key === null || worldKey === null || plain === null) return;
    let pending = recordNameCache.get(key);
    if (!pending) {
      pending = readApprovalRecord(worldKey, plain).catch(() => ({ name: null }));
      recordNameCache.set(key, pending);
    }
    let live = true;
    void pending.then((record) => {
      // Shared only while in flight: a later approval reads the name again, so a rename shows.
      if (recordNameCache.get(key) === pending) recordNameCache.delete(key);
      if (live) setRead({ key, record });
    });
    return () => {
      live = false;
    };
    // The key names the record; plain is read only through it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, worldKey]);
  return read?.key === key && key !== null
    ? { name: read.record.name, calendarEvent: read.record.calendarEvent ?? null, loaded: true }
    : { name: null, calendarEvent: null, loaded: key === null };
}

/**
 * The stored cancellation plan a call names, read from the World. A failed read
 * is a refusal shown to the person (never "still reading" forever, never a
 * guess), and is asked again the next time the card is shown.
 */
const planCache = new Map<string, Promise<SubscriptionPlanRead>>();
function useSubscriptionPlan(
  worldKey: string | null,
  ref: { readonly planKey: string } | null,
): SubscriptionPlanRead | null {
  const planKey = ref?.planKey ?? null;
  const key = worldKey && planKey ? JSON.stringify([worldKey, planKey]) : null;
  const [read, setRead] = useState<{
    readonly key: string;
    readonly value: SubscriptionPlanRead;
  } | null>(null);
  useEffect(() => {
    if (key === null || worldKey === null || planKey === null) return;
    // The card and its actions share one read while it is in flight; a later
    // card reads again, so a plan that was refused or has since expired shows.
    let pending = planCache.get(key);
    if (!pending) {
      pending = readSubscriptionPlan(worldKey, planKey);
      planCache.set(key, pending);
    }
    const shared = pending;
    let live = true;
    void shared.then((value) => {
      if (planCache.get(key) === shared) planCache.delete(key);
      if (live) setRead({ key, value });
    });
    return () => {
      live = false;
    };
  }, [key, worldKey, planKey]);
  if (key === null) return null;
  return read?.key === key ? read.value : { status: "reading" };
}

/**
 * While a price change's card is shown it is read again every few seconds, until it reads applied or closed (a re-read
 * that fails keeps the last good answer), so one declined on another card, or past its expiry, shows closed and
 * Approve is blocked (#1770 review).
 */
export const COMMERCIAL_CHANGE_REFRESH: SharedReadRefresh<CommercialChangeRead> = {
  everyMs: 5_000,
  accept: (value) => value.status === "loaded",
  // Applied or closed (declined, expired): nothing about it can change any more.
  done: (value) =>
    value.status === "loaded" &&
    (value.change.status === "applied" || value.change.closed !== undefined),
};
/** Can 68: the stored change to what Neucleos charges that a call names, read from the World; a failed read is said. */
function useCommercialChange(
  worldKey: string | null,
  ref: { readonly changeKey: string } | null,
): CommercialChangeRead | null {
  const changeKey = ref?.changeKey ?? null;
  const key =
    worldKey && changeKey ? JSON.stringify(["commercial-change", worldKey, changeKey]) : null;
  const read = useSharedRead(
    key,
    () => readCommercialChange(worldKey!, changeKey!),
    COMMERCIAL_CHANGE_REFRESH,
  );
  if (ref === null || changeKey === null) return null;
  if (worldKey === null) return { status: "failed", reason: CARD_NO_WORLD };
  return read ?? { status: "reading" };
}

/** Can 73: the stored change to who belongs to the World that an apply call names, read from the World. */
function useMemberChange(
  worldKey: string | null,
  ref: { readonly changeKey: string } | null,
): MemberChangeRead | null {
  const changeKey = ref?.changeKey ?? null;
  const key = worldKey && changeKey ? JSON.stringify(["member-change", worldKey, changeKey]) : null;
  const read = useSharedRead(key, () => readMemberChange(worldKey!, changeKey!));
  if (ref === null || changeKey === null) return null;
  if (worldKey === null) return { status: "failed", reason: CARD_NO_WORLD };
  return read ?? { status: "reading" };
}

/** PR #1625: the consultant's catalog card an audience change names, read from the World; a failed read is said. */
function useConsultingCatalog(
  worldKey: string | null,
  ref: { readonly audience: string; readonly catalogDigest: string } | null | undefined,
): ConsultingCatalogRead | null {
  // The digest is in the key: a card for another digest reads the World afresh rather than sharing an older read.
  const key =
    worldKey && ref ? JSON.stringify(["consulting-catalog", worldKey, ref.catalogDigest]) : null;
  const read = useSharedRead(key, () => readConsultingCatalog(worldKey!));
  if (!ref) return null;
  if (worldKey === null) return { status: "failed", reason: CARD_NO_WORLD };
  return read ?? { status: "reading" };
}

/** A card for a stored change that is shown outside any World cannot read it, so it says so instead of reading forever. */
export const CARD_NO_WORLD =
  "This approval is not shown in a World, so its change cannot be read here.";

/**
 * One read of a stored change, shared by everything showing it at once: the card's words and its Approve button
 * (Inbox: the row and its actions; chat: the panel and its actions) read the same value in the same render, so the
 * button can never be enabled by a read the card does not show. Reads are joined while in flight; the value is kept
 * while anything shows it and dropped when the last one closes, so a card opened later reads again.
 */
type SharedRead = {
  value: unknown;
  started: boolean;
  readonly listeners: Set<() => void>;
  // Reads are made one at a time, each numbered; an answer older than the one shown is never applied (#1770 review).
  inFlight: boolean;
  issued: number;
  applied: number;
  stop?: (() => void) | undefined;
};
const sharedReads = new Map<string, SharedRead>();
/**
 * `refresh`: read again every `everyMs` while anything shows it and the window is not hidden (and at once when it is
 * shown or focused again), one read at a time, never applying an answer older than the one shown. An answer `accept` refuses (a
 * failed read) never replaces one it took, so a failed re-read keeps the last good answer; with none yet, it is shown.
 * Once an answer is `done` (nothing more can change), reading stops. A price change's card is read again so that a
 * change declined on another of its cards, or in another conversation, closes this one too: it then reads `closed`,
 * and Approve is blocked.
 */
export type SharedReadRefresh<T> = {
  readonly everyMs: number;
  readonly accept: (value: T) => boolean;
  readonly done?: (value: T) => boolean;
};
const windowHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
export function useSharedRead<T>(
  key: string | null,
  read: () => Promise<T>,
  refresh?: SharedReadRefresh<T>,
): T | null {
  // The latest reader, so subscribing depends on the key alone (a new subscribe on every render would drop the value).
  const reader = useRef(read);
  useLayoutEffect(() => {
    reader.current = read;
  });
  const subscribe = useCallback(
    (notify: () => void) => {
      if (key === null) return () => {};
      let entry = sharedReads.get(key);
      if (!entry) {
        entry = {
          value: null,
          started: false,
          listeners: new Set(),
          inFlight: false,
          issued: 0,
          applied: 0,
        };
        sharedReads.set(key, entry);
      }
      entry.listeners.add(notify);
      if (!entry.started) {
        entry.started = true;
        const mine = entry;
        const apply = (seq: number, value: T) => {
          if (sharedReads.get(key) !== mine || seq <= mine.applied) return;
          if (
            refresh &&
            mine.applied > 0 &&
            refresh.accept(mine.value as T) &&
            !refresh.accept(value)
          )
            return;
          mine.applied = seq;
          mine.value = value;
          for (const listener of mine.listeners) listener();
          if (refresh?.done?.(value)) mine.stop?.();
        };
        const run = () => {
          if (sharedReads.get(key) !== mine || mine.inFlight) return;
          mine.inFlight = true;
          const seq = ++mine.issued;
          void reader.current().then(
            (value) => {
              mine.inFlight = false;
              apply(seq, value);
            },
            () => {
              // A read that throws changes nothing shown; the next one is made as usual.
              mine.inFlight = false;
            },
          );
        };
        if (refresh) {
          const timer = setInterval(() => {
            if (!windowHidden()) run();
          }, refresh.everyMs);
          const shown = () => {
            if (!windowHidden()) run();
          };
          if (typeof document !== "undefined") document.addEventListener("visibilitychange", shown);
          if (typeof window !== "undefined") window.addEventListener("focus", shown);
          mine.stop = () => {
            clearInterval(timer);
            if (typeof document !== "undefined")
              document.removeEventListener("visibilitychange", shown);
            if (typeof window !== "undefined") window.removeEventListener("focus", shown);
            mine.stop = undefined;
          };
        }
        run();
      }
      return () => {
        const current = sharedReads.get(key);
        if (!current) return;
        current.listeners.delete(notify);
        if (current.listeners.size === 0) {
          current.stop?.();
          sharedReads.delete(key);
        }
      };
    },
    // The refresh is fixed per call site; its everyMs is enough to tell one from another.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, refresh?.everyMs],
  );
  const snapshot = useCallback(
    () => (key === null ? null : ((sharedReads.get(key)?.value as T | null | undefined) ?? null)),
    [key],
  );
  return useSyncExternalStore(subscribe, snapshot, () => null);
}

/** What a shared read shows now for `key`, or null. */
function sharedReadValue<T>(key: string): T | null {
  return (sharedReads.get(key)?.value as T | null | undefined) ?? null;
}

/**
 * Reads `key` again now, outside its refresh (Approve checking before it acts), and shows the answer on every card
 * showing it unless a newer read has already answered. Every answer is applied: use it only for a read whose refresh
 * accepts every answer.
 */
async function readSharedNow<T>(key: string, read: () => Promise<T>): Promise<T> {
  const entry = sharedReads.get(key);
  const seq = entry ? ++entry.issued : 0;
  const value = await read();
  if (entry && sharedReads.get(key) === entry && seq > entry.applied) {
    entry.applied = seq;
    entry.value = value;
    for (const listener of entry.listeners) listener();
  }
  return value;
}

/**
 * A card that can expire is drawn again at its expiry, and whenever this window is shown again (a Mac that slept
 * fires late timers), so Approve is never left enabled on a change that has expired while the card stayed open.
 */
function useRedrawAtExpiry(expiresAt: number | null): void {
  const [, redraw] = useState(0);
  useEffect(() => {
    if (expiresAt === null) return;
    const bump = () => redraw((n) => n + 1);
    const wait = expiresAt - Date.now();
    const timer = wait > 0 && wait < 2_147_483_647 ? setTimeout(bump, wait + 50) : null;
    const onShow = () => {
      if (document.visibilityState === "visible") bump();
    };
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onShow);
    return () => {
      if (timer !== null) clearTimeout(timer);
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onShow);
    };
  }, [expiresAt]);
}

/**
 * W-7cdf5337: the member or group a Canvas share names, by name (a group with
 * its size), read from the World under the person's own grant; null when it
 * cannot be read.
 */
export async function readShareRecipientName(
  worldKey: string,
  ref: NonNullable<PlainApprovalRequest["shareRecipient"]>,
): Promise<{ readonly name: string; readonly memberCount?: number | null } | null> {
  if (ref.kind === "group") return readShareGroup(worldKey, ref.groupId);
  const members = await listConversationMembers(worldKey);
  if (!members.ok) return null;
  const member = members.members.find((row) => row.actorId === ref.actorId);
  return member ? { name: member.displayName } : null;
}
const shareRecipientCache = new Map<
  string,
  Promise<{ readonly name: string; readonly memberCount?: number | null } | null>
>();

function useShareRecipient(
  worldKey: string | null,
  ref: PlainApprovalRequest["shareRecipient"] | undefined,
): {
  readonly named: { readonly name: string; readonly memberCount?: number | null } | null;
  readonly loaded: boolean;
} {
  const kind = ref?.kind ?? null;
  const id = ref ? (ref.kind === "group" ? ref.groupId : ref.actorId) : null;
  const key = worldKey && kind && id ? JSON.stringify([worldKey, kind, id]) : null;
  const [read, setRead] = useState<{
    readonly key: string;
    readonly named: { readonly name: string; readonly memberCount?: number | null } | null;
  } | null>(null);
  useEffect(() => {
    if (key === null || worldKey === null || kind === null || id === null) return;
    let pending = shareRecipientCache.get(key);
    if (!pending) {
      pending = readShareRecipientName(
        worldKey,
        kind === "group" ? { kind, groupId: id } : { kind, actorId: id },
      ).catch(() => null);
      shareRecipientCache.set(key, pending);
    }
    let live = true;
    void pending.then((named) => {
      // Shared only while in flight: a later approval reads the name again, so a rename shows.
      if (shareRecipientCache.get(key) === pending) shareRecipientCache.delete(key);
      if (live) setRead({ key, named });
    });
    return () => {
      live = false;
    };
  }, [key, worldKey, kind, id]);
  return read?.key === key && key !== null
    ? { named: read.named, loaded: true }
    : { named: null, loaded: key === null };
}

/** Said when the card has no World to list a share's Components and data from. */
export const CANVAS_SHARE_DATA_NO_WORLD =
  "This approval is not linked to a World, so the Components and data this share gives cannot be listed or approved here.";
const shareDataCache = new Map<string, Promise<CanvasShareDataRead>>();

/**
 * W-7cdf5337: the Components and data a Canvas share with data gives, read
 * from the World for the exact revision and changes the call names. Null when
 * the call names none. A failed read is shown to the person, never "still
 * reading" forever, and is asked again the next time the card is shown.
 */
function useCanvasShareData(
  worldKey: string | null,
  ref: CanvasShareDataRef | null | undefined,
): CanvasShareDataRead | null {
  const canvasId = ref?.canvasId ?? null;
  const sequence = ref?.sequence ?? null;
  const dataChanges = ref?.dataChanges === true;
  const key =
    worldKey && canvasId !== null && sequence !== null
      ? JSON.stringify([worldKey, canvasId, sequence, dataChanges])
      : null;
  const [read, setRead] = useState<{
    readonly key: string;
    readonly value: CanvasShareDataRead;
  } | null>(null);
  useEffect(() => {
    if (key === null || worldKey === null || canvasId === null || sequence === null) return;
    // The card and its actions share one read while it is in flight; a later
    // card reads again, so a Canvas that has changed since shows.
    let pending = shareDataCache.get(key);
    if (!pending) {
      pending = readCanvasShareData(worldKey, { canvasId, sequence, dataChanges });
      shareDataCache.set(key, pending);
    }
    const shared = pending;
    let live = true;
    void shared.then((value) => {
      if (shareDataCache.get(key) === shared) shareDataCache.delete(key);
      if (live) setRead({ key, value });
    });
    return () => {
      live = false;
    };
  }, [key, worldKey, canvasId, sequence, dataChanges]);
  if (!ref) return null;
  if (worldKey === null) return { status: "failed", reason: CANVAS_SHARE_DATA_NO_WORLD };
  return read?.key === key ? read.value : { status: "reading" };
}

/**
 * The website pages card's read: the exact set a publication names, each path changed, added or kept against the page
 * live now. Null when the call names no such set. A failed read is shown, never "still reading" forever, and the card
 * and its Approve share one read while it is in flight.
 */
function useWebsitePages(
  worldKey: string | null,
  ref: WebsitePagesRef | null,
): WebsitePagesRead | null {
  // The key carries the whole ref.
  const key = worldKey && ref ? JSON.stringify(["website-pages", worldKey, ref]) : null;
  const read = useSharedRead(key, () => readWebsitePages(worldKey!, ref!));
  if (!ref) return null;
  if (worldKey === null) return { status: "failed", reason: WEBSITE_PAGES_UNREAD };
  return read ?? { status: "reading" };
}

/**
 * #1844 Codex pass 1: while a release card is shown it reads the World's review again every 15 seconds and when the
 * window is shown or focused, and Approve reads it once more before it acts, so a release superseded or moved under an
 * open card turns Approve off. Every answer replaces the last (a failed re-read too): the card never keeps an Approve the
 * World would now refuse.
 */
export const COMPONENT_RELEASE_REFRESH: SharedReadRefresh<ComponentReleaseRead> = {
  everyMs: 15_000,
  accept: () => true,
};
const componentReleaseKey = (worldKey: string | null, ref: ComponentReleaseRef | null) =>
  worldKey && ref ? JSON.stringify(["component-release", worldKey, ref]) : null;
/** The review read again, judged against what the card showed before it (componentReleaseAfter). */
const rereadComponentRelease = (key: string, worldKey: string, ref: ComponentReleaseRef) =>
  readComponentRelease(worldKey, ref).then((next) =>
    componentReleaseAfter(sharedReadValue<ComponentReleaseRead>(key), next),
  );
/**
 * Before a release card's Approve acts: its review read once more, and why it cannot be approved now, or null. Null
 * (not a promise) when the call names no release, so every other card's Approve acts at once, as before.
 */
export function recheckComponentRelease(
  worldKey: string | null,
  ref: ComponentReleaseRef | null | undefined,
): Promise<string | null> | null {
  const key = componentReleaseKey(worldKey, ref ?? null);
  if (key === null || !ref || worldKey === null) return null;
  return readSharedNow(key, () => rereadComponentRelease(key, worldKey, ref)).then(
    (read) => componentReleaseBlocked(ref, read),
    () => COMPONENT_RELEASE_UNREAD,
  );
}

/**
 * Step 3 K2c2: the World's review of the platform Components release a publication names, for its exact builds. Null
 * when the call names none; a failed read is shown, and the card and its Approve share one read while it is in flight.
 */
function useComponentRelease(
  worldKey: string | null,
  ref: ComponentReleaseRef | null,
): ComponentReleaseRead | null {
  const key = componentReleaseKey(worldKey, ref);
  const read = useSharedRead(
    key,
    () => rereadComponentRelease(key!, worldKey!, ref!),
    COMPONENT_RELEASE_REFRESH,
  );
  if (!ref) return null;
  if (worldKey === null) return { status: "failed", reason: COMPONENT_RELEASE_UNREAD };
  return read ?? { status: "reading" };
}

/** The booking type a meeting-link change names, from the host's own list, read when the card asks; a failure is said. */
export async function readApprovalBookingType(
  worldKey: string,
  bookingTypeId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<BookingTypeRead> {
  const listed = await listBookingTypes(worldKey, fetchImpl).catch(() => null);
  const row = listed?.ok
    ? listed.bookingTypes.find((type) => type.bookingTypeId === bookingTypeId)
    : undefined;
  return row
    ? {
        status: "loaded",
        title: row.title,
        meetingUrl: typeof row.meetingUrl === "string" ? row.meetingUrl : null,
      }
    : { status: "failed" };
}
function useBookingTypeRead(
  worldKey: string | null,
  ref: { readonly bookingTypeId: string } | null,
): BookingTypeRead | null | undefined {
  const bookingTypeId = ref?.bookingTypeId ?? null;
  const key =
    worldKey && bookingTypeId ? JSON.stringify(["booking-type", worldKey, bookingTypeId]) : null;
  const read = useSharedRead(key, () => readApprovalBookingType(worldKey!, bookingTypeId!));
  if (bookingTypeId === null) return undefined;
  if (worldKey === null) return { status: "failed" };
  return read;
}

/** When a card's Approve stops on its own: the expiry of the prepared change it names, once read; else null. */
export function approvalExpiresAt(
  context: Pick<ApprovalRequestContext, "memberChange" | "commercialChange">,
): number | null {
  const { memberChange, commercialChange } = context;
  if (memberChange?.status === "loaded") return memberChange.change.expiresAt;
  if (commercialChange?.status === "loaded") return commercialChange.change.expiresAt;
  return null;
}

/** The names a call's approval needs, and whether they are still being read. */
export function useApprovalRequestContext(
  worldKey: string | null,
  request: ApprovalRequestInput | null | undefined,
  /**
   * Who is asking: the card's own identity (a gate instance, a request id). The 5 s wait belongs to one request, so
   * another request that names the same Canvas or conversation reads for its own 5 s, not the first one's spent wait.
   */
  identity?: string | null,
): {
  readonly context: ApprovalRequestContext;
  readonly reading: boolean;
  /** Read before Approve acts: why it cannot act now, or null; itself null when nothing needs reading first. */
  readonly recheck: () => Promise<string | null> | null;
} {
  // W-dc12e842: only the Desk's own structured call names anything to read; its text never does.
  const plain = request ? describedApprovalRequest(request).plain : null;
  const canvasId = worldKey ? (plain?.canvasId ?? null) : null;
  const conversationId = worldKey ? (plain?.conversationId ?? null) : null;
  const canvas = useCanvasHead(worldKey, canvasId);
  const recipients = useConversationRecipients(worldKey, conversationId);
  const record = useRecordName(worldKey, plain);
  const subscriptionPlan = useSubscriptionPlan(worldKey, plain?.subscriptionPlan ?? null);
  const commercialChange = useCommercialChange(worldKey, plain?.commercialChange ?? null);
  const memberChange = useMemberChange(worldKey, plain?.memberChange ?? null);
  const consultingCatalog = useConsultingCatalog(worldKey, plain?.consultingCatalog);
  useRedrawAtExpiry(approvalExpiresAt({ memberChange, commercialChange }));
  const shareRecipient = useShareRecipient(worldKey, plain?.shareRecipient);
  const canvasShareData = useCanvasShareData(worldKey, plain?.canvasShareData);
  const websitePages = useWebsitePages(worldKey, plain?.websitePages ?? null);
  const componentRelease = useComponentRelease(worldKey, plain?.componentRelease ?? null);
  const bookingType = useBookingTypeRead(worldKey, plain?.bookingType ?? null);
  const worldName = useNeucleosWorldStore(
    (state) => state.worlds.find((world) => world.worldKey === worldKey)?.name ?? null,
  );
  const waiting =
    (canvasId !== null && !canvas.loaded) ||
    !recipients.loaded ||
    !record.loaded ||
    !shareRecipient.loaded ||
    // A booking type's name and link now never hold the card: its every argument shows at once, and the
    // name and current link are added when (if) they arrive, so Approve never meets an empty card.
    canvasShareData?.status === "reading" ||
    websitePages?.status === "reading" ||
    componentRelease?.status === "reading";
  const [waited, setWaited] = useState<string | null>(null);
  const waitKey = waiting
    ? `${identity ?? ""}|${worldKey}|${canvasId}|${conversationId}|${JSON.stringify(plain?.crmRecord ?? plain?.crmFollowUp ?? plain?.calendarOccurrence ?? null)}|${JSON.stringify(plain?.shareRecipient ?? null)}|${JSON.stringify(plain?.canvasShareData ?? null)}|${JSON.stringify(plain?.websitePages ?? null)}|${JSON.stringify(plain?.componentRelease ?? null)}`
    : null;
  useEffect(() => {
    if (waitKey === null) return;
    const timer = setTimeout(() => setWaited(waitKey), APPROVAL_CONTEXT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waitKey]);
  return {
    context: {
      canvasName: canvas.read?.canvas.name ?? null,
      recipients: recipients.names,
      worldName,
      recordName: record.name,
      calendarEvent: record.calendarEvent,
      shareRecipient: shareRecipient.named,
      canvasShareData,
      websitePages,
      componentRelease,
      subscriptionPlan,
      commercialChange,
      memberChange,
      ...(consultingCatalog != null ? { consultingCatalog } : {}),
      ...(bookingType != null ? { bookingType } : {}),
      timeZone: deviceTimeZone(),
    },
    reading: waiting && waited !== waitKey,
    recheck: () => recheckComponentRelease(worldKey, plain?.componentRelease),
  };
}
